"use client";

import * as React from "react";
import { ChevronDown, X } from "lucide-react";

import { withOccurrenceKeys } from "../../lib/occurrence-keys";
import { cn } from "../../lib/utils";
import type { SelectionMode } from "./data-table-hooks";
import { ActionsMenu } from "./actions-menu";
import { Button } from "./button";
import { Tooltip } from "./tooltip";

// ---------------------------------------------------------------------------
// Root -- fixed bottom bar showing "N row(s) selected"
// ---------------------------------------------------------------------------

type MultiSelectActionBarRootProps = {
	/** Number of explicitly selected rows (ignored when mode is all-matching-filter). When 0, the bar is hidden. */
	count: number;
	/** Total rows matching the current filter (required when mode is all-matching-filter for the label). */
	totalCount?: number;
	mode?: SelectionMode;
	/** When true, shows a loading label instead of the selection count. */
	isLoading?: boolean;
	/** Clears selection on Escape (skipped when focus is inside dialogs/menus). */
	onClearSelection?: () => void;
	children?: React.ReactNode;
	className?: string;
	"data-testid"?: string;
};

function MultiSelectActionBarRoot({
	count,
	totalCount,
	mode = "explicit",
	isLoading = false,
	onClearSelection,
	children,
	className,
	"data-testid": dataTestId = "multi-select-action-bar",
}: MultiSelectActionBarRootProps) {
	const isAllSelected = mode === "all-matching-filter";
	const displayCount = isAllSelected ? (totalCount ?? count) : count;

	React.useEffect(() => {
		if (displayCount === 0 || !onClearSelection) {
			return;
		}
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key !== "Escape") {
				return;
			}
			if (event.defaultPrevented) {
				return;
			}
			const rawTarget = event.target;
			const active =
				rawTarget instanceof Element
					? rawTarget
					: document.activeElement instanceof Element
						? document.activeElement
						: null;
			if (!active) {
				onClearSelection();
				return;
			}
			if (
				active.closest(
					'[role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"]',
				)
			) {
				return;
			}
			if (
				active.matches(
					'input, textarea, select, option, button, [contenteditable="true"], [role="textbox"], [role="searchbox"]',
				) ||
				(active instanceof HTMLElement && active.isContentEditable)
			) {
				return;
			}
			onClearSelection();
		};
		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [displayCount, onClearSelection]);

	if (displayCount === 0) {
		return null;
	}

	const selectionLabel = isLoading
		? `Loading ${displayCount.toLocaleString()} row(s)…`
		: `${displayCount.toLocaleString()} row(s) selected`;

	return (
		<div
			className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2"
			data-testid={dataTestId}
		>
			<div
				className={cn(
					"flex items-center gap-4 rounded-lg bg-secondary-600 py-3 pl-6 pr-3 shadow-[0px_4px_7px_0px_rgba(15,23,42,0.2)]",
					className,
				)}
			>
				<span className="mr-1 text-xs font-semibold text-white">
					{selectionLabel}
				</span>
				{children}
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// SelectAll -- "Select all (N)" button
// ---------------------------------------------------------------------------

type MultiSelectActionBarSelectAllProps = {
	/** Total number of items available to select. */
	count: number;
	/** Called when the user clicks "Select all". */
	onSelect: () => void;
	className?: string;
	disabled?: boolean;
	disabledReason?: string;
};

function MultiSelectActionBarSelectAll({
	count,
	onSelect,
	className,
	disabled = false,
	disabledReason,
}: MultiSelectActionBarSelectAllProps) {
	const disabledDescriptionId = React.useId();
	const button = (
		<Button
			variant="outline-muted-primary"
			size="sm"
			onClick={onSelect}
			className={className}
			disabled={disabled}
			type="button"
		>
			Select all ({count.toLocaleString()})
		</Button>
	);

	if (disabled && disabledReason) {
		return (
			<Tooltip content={disabledReason} asChild>
				<span
					className="inline-flex"
					// eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- disabled buttons ignore events; wrapper stays focusable for the tooltip
					tabIndex={0}
					aria-disabled="true"
					aria-describedby={disabledDescriptionId}
				>
					<span id={disabledDescriptionId} className="sr-only">
						{disabledReason}
					</span>
					{button}
				</span>
			</Tooltip>
		);
	}

	return button;
}

// ---------------------------------------------------------------------------
// ClearAll -- "Clear all" button with X icon
// ---------------------------------------------------------------------------

type MultiSelectActionBarClearAllProps = {
	/** Called when the user clicks "Clear all". */
	onClear: () => void;
	className?: string;
};

function MultiSelectActionBarClearAll({
	onClear,
	className,
}: MultiSelectActionBarClearAllProps) {
	return (
		<Button
			variant="outline-muted-primary"
			size="sm"
			onClick={onClear}
			className={className}
			type="button"
		>
			<X className="size-3" />
			Clear all
		</Button>
	);
}

// ---------------------------------------------------------------------------
// Separator -- vertical divider between sections
// ---------------------------------------------------------------------------

function MultiSelectActionBarSeparator({ className }: { className?: string }) {
	return <div className={cn("h-8 w-px bg-white/20", className)} />;
}

// ---------------------------------------------------------------------------
// Actions -- slot for action buttons / menus
// ---------------------------------------------------------------------------

type MultiSelectActionBarActionsProps = {
	children: React.ReactNode;
	className?: string;
};

function MultiSelectActionBarActions({
	children,
	className,
}: MultiSelectActionBarActionsProps) {
	return (
		<div className={cn("flex items-center gap-2", className)}>{children}</div>
	);
}

// ---------------------------------------------------------------------------
// ActionsMenu -- dropdown aligned with admin-app TableMultiSelectActionBar
// ---------------------------------------------------------------------------

export type MultiSelectActionBarMenuItem = {
	type: "item";
	label: React.ReactNode;
	disabled?: boolean;
	disabledReason?: string;
	onSelect?: () => void | Promise<void>;
	variant?: "default" | "destructive";
	"data-testid"?: string;
	/** Wraps the actions menu when this item is present (e.g. Popover anchored to trigger). */
	actionsWrapper?: (actionsMenu: React.ReactNode) => React.ReactNode;
};

type MultiSelectActionBarActionsMenuProps = {
	items: MultiSelectActionBarMenuItem[];
	isLoading?: boolean;
	actionDialogs?: React.ReactNode;
	align?: "start" | "center" | "end";
};

function MultiSelectActionBarActionsMenu({
	items,
	isLoading = false,
	actionDialogs,
	align = "start",
}: MultiSelectActionBarActionsMenuProps) {
	const wrappers = items.flatMap((a) =>
		a.actionsWrapper ? [a.actionsWrapper] : [],
	);

	const actionsMenu = (
		<>
			{actionDialogs}
			<ActionsMenu>
				<ActionsMenu.Trigger asChild>
					<Button variant="primary" size="sm" loading={isLoading} type="button">
						Actions
						<ChevronDown className="size-3" />
					</Button>
				</ActionsMenu.Trigger>
				<ActionsMenu.Content align={align}>
					{withOccurrenceKeys(
						items,
						(item) =>
							item["data-testid"] ??
							(typeof item.label === "string" ? item.label : ""),
					).map(({ item, key }) => {
						if (item.type !== "item") {
							return null;
						}
						// eslint-disable-next-line @typescript-eslint/no-unused-vars -- the rest pattern drops the non-DOM fields
						const { actionsWrapper: _w, type: _t, label, ...itemProps } = item;
						return (
							<ActionsMenu.Item key={key} {...itemProps}>
								{label}
							</ActionsMenu.Item>
						);
					})}
				</ActionsMenu.Content>
			</ActionsMenu>
		</>
	);

	return wrappers.reduce<React.ReactNode>(
		(wrapped, wrapper) => wrapper(wrapped),
		actionsMenu,
	);
}

// ---------------------------------------------------------------------------
// Compound export
// ---------------------------------------------------------------------------

export const MultiSelectActionBar = Object.assign(MultiSelectActionBarRoot, {
	SelectAll: MultiSelectActionBarSelectAll,
	ClearAll: MultiSelectActionBarClearAll,
	Separator: MultiSelectActionBarSeparator,
	Actions: MultiSelectActionBarActions,
	ActionsMenu: MultiSelectActionBarActionsMenu,
});

export type {
	MultiSelectActionBarRootProps,
	MultiSelectActionBarSelectAllProps,
	MultiSelectActionBarClearAllProps,
	MultiSelectActionBarActionsProps,
	MultiSelectActionBarActionsMenuProps,
};

export type { SelectionMode } from "./data-table-hooks";
